import { useNavigate } from 'react-router-dom';
import type { Size2MixPrimaryNavId, Size2MixSecondaryNavItem, NetworkPageId } from './types';

/** Slugs of the Size2Mix reference prototypes (`app/front/src/prototypes/size2mix/ref-*`). */
export const SIZE2MIX_REF_SLUGS = {
    home: 'ref-home',
    networkList: 'ref-network-list',
    contracts: 'ref-contracts',
    coreai: 'ref-coreai',
    general: 'ref-network-general',
    'import-export': 'ref-network-import-export',
    topology: 'ref-topology-parameters',
    topologyEditor: 'ref-topology-editor',
    topologySynthesis: 'ref-topology-synthesis',
    production: 'ref-network-production',
    'production-fuels': 'ref-production-fuels',
    'production-electricity': 'ref-production-electricity',
    'production-synthesis': 'ref-production-synthesis',
    plannings: 'ref-network-plannings',
    optimisations: 'ref-optimisation-list',
    optimisationDetail: 'ref-optimisation-detail',
} as const;

export type Size2MixRefTarget = keyof typeof SIZE2MIX_REF_SLUGS;

export const size2mixRefPath = (target: Size2MixRefTarget) => `/size2mix/prototype/${SIZE2MIX_REF_SLUGS[target]}`;

const PRIMARY_TARGET: Partial<Record<Size2MixPrimaryNavId, Size2MixRefTarget>> = {
    networks: 'networkList',
    contracts: 'contracts',
    coreai: 'coreai',
};

/**
 * Navigation between reference prototypes, so the shell behaves like the real app.
 * In your own prototype, either reuse it (to jump to the references) or pass your own handlers.
 */
export function useSize2MixRefNavigation() {
    const navigate = useNavigate();
    const goTo = (target: Size2MixRefTarget) => navigate(size2mixRefPath(target));
    return {
        goTo,
        onPrimaryNavigate: (id: Size2MixPrimaryNavId) => {
            const target = PRIMARY_TARGET[id];
            if (target) goTo(target);
        },
        onNetworkNavigate: (page: NetworkPageId) => goTo(page),
    };
}

/** Tabs of Réseau › Topologie: each tab is its own reference prototype. */
export const TOPOLOGY_REF_TABS = [
    { id: 'parameters', label: 'Paramètres', target: 'topology' },
    { id: 'editor', label: 'Éditeur réseau', target: 'topologyEditor' },
    { id: 'synthesis', label: 'Synthèse', target: 'topologySynthesis' },
] as const satisfies ReadonlyArray<{ id: string; label: string; target: Size2MixRefTarget }>;
export type TopologyRefTabId = (typeof TOPOLOGY_REF_TABS)[number]['id'];

/** Tabs of Réseau › Production: each tab is its own reference prototype. */
export const PRODUCTION_REF_TABS = [
    { id: 'editor', label: 'Éditeur de production', target: 'production' },
    { id: 'fuels', label: 'Combustibles', target: 'production-fuels' },
    { id: 'electricity', label: 'Électricité auxiliaires', target: 'production-electricity' },
    { id: 'synthesis', label: 'Synthèse', target: 'production-synthesis' },
] as const satisfies ReadonlyArray<{ id: string; label: string; target: Size2MixRefTarget }>;
export type ProductionRefTabId = (typeof PRODUCTION_REF_TABS)[number]['id'];

/** Secondary nav of the "Réseaux" primary entry (Accueil + Liste des réseaux). Ids are ref targets. */
export const NETWORKS_SECONDARY_NAV: Size2MixSecondaryNavItem<'home' | 'networkList'>[] = [
    { id: 'home', icon: 'home', label: 'Accueil' },
    { id: 'networkList', icon: 'list', label: 'Liste des réseaux' },
];
